import React, { useEffect, useState } from "react"
import { Link } from "react-router"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { httpOk, GET, FILE_D } from 'helpers/api'
import { findKeyFromArrayByValue, formatMoney, regexPhoneNumber, returnSign, formatDate, PAGE_SIZES } from 'helpers/helpers'
import { businessToday, businessMonthStart } from 'helpers/businessDay'

function Index() {
	const { t } = useTranslation()

	const statuses = [
		{ id: 1, name: t('open') },
		{ id: 10, name: t('closed') },
		{ id: -1, name: t('deleted') },
	]
	const counterpartyTypes = [
		{ id: 'client', name: t('client') },
		{ id: 'employee', name: t('employee') },
		{ id: 'organization', name: t('organization') },
		{ id: 'supplier', name: t('supplier') },
	]

	const [data, setData] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: businessMonthStart(),
		end_date: businessToday(),
		status: 10,
		entity_debtor_type: null,
		search: '',
		size: 20,
	})
	const [pagination, setPagination] = useState({
		url: '/cheques-by-counterparties',
		size: 20,
		response: null,
	})

	function buildFilterParams(filterDataCopy = filterData, includePage = true) {
		let urlParams = ""

		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.status)
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.entity_debtor_type)
			urlParams += returnSign(urlParams) + 'entity_debtor_type=' + filterDataCopy.entity_debtor_type
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.size)
			urlParams += returnSign(urlParams) + 'size=' + filterDataCopy.size
		if (includePage && filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		return urlParams
	}

	async function searchWithFilter(nextFilterData = null) {
		const filterDataCopy = { ...(nextFilterData || filterData) }
		let filterUrl = "/cheques-by-counterparties"
		let urlParams = buildFilterParams(filterDataCopy)
		checkFilter(filterDataCopy)

		filterDataCopy.urlParams = urlParams

		if (`/cheques-by-counterparties${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, url: filterUrl, size: filterDataCopy.size, response: response.data })
		}

		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	function handleSearchChange(search) {
		const nextFilterData = { ...filterData, search, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function downloadExcelFile() {
		let urlParams = buildFilterParams({ ...filterData }, false)
		urlParams += returnSign(urlParams) + 'excel=true'
		const response = await FILE_D(`/cheques-by-counterparties${urlParams}`, { loader: true })
		if (httpOk(response)) {
			const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', `cheques-by-counterparties.xlsx`)
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	function resetFilter() {
		const nextFilterData = {
			start_date: businessMonthStart(),
			end_date: businessToday(),
			status: 10,
			entity_debtor_type: null,
			search: '',
			size: 20,
		}
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function checkFilter(currentFilterData = filterData) {
		if (
			currentFilterData.start_date === null &&
			currentFilterData.end_date === null &&
			currentFilterData.status === null &&
			currentFilterData.entity_debtor_type === null &&
			currentFilterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function paginate(page) {
		const nextFilterData = { ...filterData, page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function handlePageSizeChange(size) {
		const nextFilterData = { ...filterData, size, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function detailUrl(item) {
		const params = new URLSearchParams()
		if (filterData.start_date) params.set('start_date', filterData.start_date)
		if (filterData.end_date) params.set('end_date', filterData.end_date)
		if (filterData.status) params.set('status', filterData.status)
		return `/cheques-by-counterparties/${item.entity_debtor_type}/${item.entity_debtor_id}?${params.toString()}`
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('counterparty_cheques')} [{pagination?.response?.total}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<FilterSearch
								value={filterData.search}
								onSearchChange={handleSearchChange}>
							</FilterSearch>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={statuses}
													value={statuses.find(option => option.id === filterData.status) || ''}
													onChange={(option) => setFilterData({ ...filterData, status: option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('counterparty')}</label>
												<Select
													isClearable
													options={counterpartyTypes}
													value={counterpartyTypes.find(option => option.id === filterData.entity_debtor_type) || ''}
													onChange={(option) => setFilterData({ ...filterData, entity_debtor_type: option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end">
													<button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter({ ...filterData, page: null })}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button className="btn btn-outline-primary btn-wide-2"
									onClick={() => downloadExcelFile()}>
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{(filterData.start_date && filterData.end_date) && <span>|</span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{findKeyFromArrayByValue(statuses, 'id', filterData.status, 'name')}
									</span>
								}
								{filterData.entity_debtor_type &&
									<span className="me-2">
										<b>{t('counterparty')}: </b>
										{findKeyFromArrayByValue(counterpartyTypes, 'id', filterData.entity_debtor_type, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '15%' }}>{t('type')}</th>
									<th style={{ width: '20%' }}>{t('counterparty')}</th>
									<th style={{ width: '15%' }}>{t('phone')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('cheques')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('return')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('gross_income')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('debt')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td>{findKeyFromArrayByValue(counterpartyTypes, 'id', item.entity_debtor_type, 'name')}</td>
										<td className="td-ellipsis">
											<Link to={detailUrl(item)} target="_blank" rel="noopener noreferrer" className="text-primary">
												{item.entity_name || item.entity_debtor_id}
											</Link>
										</td>
										<td>{item.entity_phone ? regexPhoneNumber(item.entity_phone) : ''}</td>
										<td className="text-end">{item.count_cheques}</td>
										<td className="text-end">{formatMoney(item.total_products)}</td>
										<td className="text-end">{formatMoney(item.total_amount_service)}</td>
										<td className="text-end">{Number(item.returned_amount) > 0 ? formatMoney(item.returned_amount) : ''}</td>
										<td className="text-end">{formatMoney(item.gross_profit)}</td>
										<td className="text-end">{formatMoney(item.total_debtor_amount)}</td>
									</tr>
								))}
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_cheques)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_products)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.sales_amount)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.returned_amount)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.gross_profit)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_debtor_amount)}</b></td>
								</tr>
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}
							showPageSizeSelect
							pageSizeOptions={PAGE_SIZES}
							onPageSizeChange={handlePageSizeChange}>
						</FilterPagination>
					}
				</div>
			</div>
		</>
	)
}

export default Index
